import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import { obligationAuthorityChipClass, obligationAuthorityLabel } from "@/modules/despacho/despachoFiscalInfoHelpers";
import {
  DESPACHO_READ_ONLY_BADGE,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

const STATUS_LABELS = {
  ok: "Coincide",
  mismatch: "Diferencia",
  workspace_override: "Ajuste del despacho",
  no_reference: "Sin referencia fija",
};

const STATUS_VARIANT = {
  ok: "secondary",
  mismatch: "destructive",
  workspace_override: "outline",
  no_reference: "outline",
};

export default function CalendarioSatValidationPanel({ testId = "calendario-sat-validation" }) {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  const [year, setYear] = useState(2026);
  const [availableYears, setAvailableYears] = useState([2026]);
  const [validation, setValidation] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const loadValidation = useCallback(async (selectedYear = year) => {
    setLoadState("loading");
    setLoadMessage("");
    try {
      const ref = await despachoFiscalInfoApi.getSatReferenceCatalog(selectedYear).catch(() => null);
      if (ref?.available_years?.length) {
        setAvailableYears(ref.available_years);
      }
      const data = await despachoFiscalInfoApi.validateFiscalDueRules({ year: selectedYear });
      setValidation(data);
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      setLoadMessage(parseFiscalInfoError(err));
    }
  }, [year]);

  useEffect(() => {
    loadValidation(year);
  }, [year, loadValidation]);

  const mismatchItems = useMemo(
    () => (validation?.items || []).filter((row) => row.status === "mismatch"),
    [validation],
  );

  const handleApplyAll = async () => {
    if (readOnly || !mismatchItems.length) return;
    setBusy(true);
    try {
      const result = await despachoFiscalInfoApi.applySatReferenceUpdates({
        year,
        only_global: true,
      });
      const count = result?.applied_count || 0;
      const skipped = result?.skipped_workspace_override_ids?.length || 0;
      if (count > 0) {
        toast.success(`Se actualizaron ${count} regla(s) global(es) según referencia SAT ${year}.`);
      } else {
        toast.info("No había reglas globales pendientes de alinear.");
      }
      if (skipped > 0) {
        toast.message(`${skipped} diferencia(s) tienen override del despacho; revíselas manualmente.`);
      }
      await loadValidation(year);
    } catch (err) {
      toast.error(parseFiscalInfoError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleApplyOne = async (obligationCatalogId) => {
    if (readOnly) return;
    setBusy(true);
    try {
      await despachoFiscalInfoApi.applySatReferenceUpdates({
        year,
        obligation_catalog_ids: [obligationCatalogId],
        only_global: true,
      });
      toast.success("Regla global actualizada.");
      await loadValidation(year);
    } catch (err) {
      toast.error(parseFiscalInfoError(err));
    } finally {
      setBusy(false);
    }
  };

  const summary = validation?.summary || {};

  return (
    <div className="space-y-4" data-testid={testId}>
      <DespachoInfoCallout>
        Compara las reglas efectivas del calendario fiscal contra un catálogo de referencia documentado
        (LIVA, RMF 2026). DIOT usa día 17 como objetivo del despacho; la referencia también documenta
        la alternativa RMF 4.5.1 (último día del mes). Las actualizaciones automáticas solo modifican
        reglas globales; los overrides del despacho se conservan.
      </DespachoInfoCallout>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Ejercicio</span>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(availableYears.length ? availableYears : [2026]).map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => loadValidation(year)}
          disabled={loadState === "loading" || busy}
        >
          {loadState === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Actualizar comparativa
        </Button>
        {!readOnly && mismatchItems.length > 0 && (
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs"
            onClick={handleApplyAll}
            disabled={busy}
            data-testid="sat-validation-apply-all"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wrench className="h-3.5 w-3.5 mr-1" />}
            Aplicar referencia ({mismatchItems.length})
          </Button>
        )}
        {readOnly && (
          <Badge variant="outline" className="text-[10px]">{DESPACHO_READ_ONLY_BADGE}</Badge>
        )}
      </div>

      {validation?.reference_title && (
        <p className="text-xs text-muted-foreground">
          {validation.reference_title}
          {validation.reference_version ? ` · v${validation.reference_version}` : ""}
        </p>
      )}

      {loadState === "error" && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
          <span>{loadMessage || "No se pudo cargar la validación."}</span>
        </div>
      )}

      {loadState === "ready" && validation?.available && (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">Coinciden: {summary.ok || 0}</Badge>
            <Badge variant="destructive">Diferencias: {summary.mismatch || 0}</Badge>
            <Badge variant="outline">Ajuste del despacho: {summary.workspace_override || 0}</Badge>
            <Badge variant="outline">Sin referencia fija: {summary.no_reference || 0}</Badge>
          </div>

          <DespachoSimpleTable
            columns={[
              { key: "nombre", header: "Obligación", className: "min-w-[180px]" },
              { key: "authority", header: "Autoridad", className: "w-[90px]" },
              { key: "status", header: "Estado", className: "w-[120px]" },
              { key: "current", header: "Regla actual", className: "min-w-[160px]" },
              { key: "reference", header: "Referencia SAT", className: "min-w-[160px]" },
              { key: "notes", header: "Notas", className: "min-w-[200px]" },
              { key: "actions", header: "", className: "w-[100px]" },
            ]}
            rows={(validation.items || []).map((row) => ({
              key: row.obligation_catalog_id,
              cells: {
                nombre: row.nombre_visible,
                authority: (
                  <Badge variant="outline" className={`text-[10px] ${obligationAuthorityChipClass(row.authority)}`}>
                    {obligationAuthorityLabel(row.authority)}
                  </Badge>
                ),
                status: (
                  <Badge variant={STATUS_VARIANT[row.status] || "outline"} className="text-[10px]">
                    {row.status === "ok" && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                    {STATUS_LABELS[row.status] || row.status}
                  </Badge>
                ),
                current: row.current?.rule_label || "—",
                reference: row.reference?.rule_label || "—",
                notes: row.reference_notes || (row.legal_sources || []).join(" · ") || "—",
                actions: row.status === "mismatch" && !row.has_workspace_override && !readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] px-2"
                    disabled={busy}
                    onClick={() => handleApplyOne(row.obligation_catalog_id)}
                  >
                    Aplicar
                  </Button>
                ) : row.has_workspace_override ? (
                  <span className="text-[10px] text-muted-foreground">Ajuste del despacho</span>
                ) : null,
              },
            }))}
            emptyMessage="No hay obligaciones fiscales para comparar."
          />
        </>
      )}
    </div>
  );
}
